/**
 * Feature Flags Configuration
 * 
 * In development mode, all features are enabled by default unless
 * specifically testing the feature flag system itself.
 * 
 * Environment Variables:
 * - NODE_ENV: 'development' | 'production' | 'test'
 * - NEXT_PUBLIC_DEV_MODE: '1' to force development mode behavior
 * - NEXT_PUBLIC_TEST_FEATURE_FLAGS: '1' to test feature flags even in dev mode
 */

// =============================================================================
// Development Mode Detection
// =============================================================================

/**
 * Check if we're in development mode.
 * Development mode is enabled if:
 * - NODE_ENV is 'development'
 * - OR NEXT_PUBLIC_DEV_MODE is '1'
 */
export const isDevelopmentMode = 
  process.env.NODE_ENV === 'development' || 
  process.env.NEXT_PUBLIC_DEV_MODE === '1'

/**
 * Check if we should enforce feature flags even in development.
 * Useful for testing the feature flag system itself.
 */
export const shouldTestFeatureFlags = process.env.NEXT_PUBLIC_TEST_FEATURE_FLAGS === '1'

/**
 * Check if features should be unrestricted.
 * In development mode, all features are enabled unless testing feature flags.
 */
export const areAllFeaturesEnabled = isDevelopmentMode && !shouldTestFeatureFlags

// =============================================================================
// Feature Flag Definitions
// =============================================================================

export enum FeatureFlag {
  // EVC Features
  EVC_BATCH_TRANSACTIONS = 'evc_batch_transactions',
  EVC_SIMULATION = 'evc_simulation',
  EVC_DEBUG_TRACING = 'evc_debug_tracing',
  
  // Native HYPE Support
  NATIVE_HYPE_DEPOSITS = 'native_hype_deposits',
  NATIVE_HYPE_WITHDRAWALS = 'native_hype_withdrawals',
  NATIVE_HYPE_BORROWS = 'native_hype_borrows',
  
  // Multiply/Leverage
  MULTIPLY_POSITIONS = 'multiply_positions',
  
  // Earn/Supply
  EEARN_VAULTS = 'eearn_vaults',
  
  // Swap
  EULER_SWAP = 'euler_swap',
  
  // Advanced
  SUB_ACCOUNTS = 'sub_accounts',
  PERMIT2 = 'permit2',

  // Card
  SHOW_WAITLIST_REFERRALS = 'show_waitlist_referrals',
  CARD_HOOK_MANAGEMENT = 'card_hook_management',
}

// =============================================================================
// Feature Flag Configuration
// =============================================================================

interface FeatureFlagConfig {
  /** Whether the feature is enabled in production */
  enabledInProduction: boolean
  /** Override for development mode. When false, the flag stays off even in dev. Defaults to true (follows areAllFeaturesEnabled). */
  enabledInDevelopment?: boolean
  /** Optional description of the feature */
  description?: string
}

const featureFlagConfig: Record<FeatureFlag, FeatureFlagConfig> = {
  [FeatureFlag.EVC_BATCH_TRANSACTIONS]: {
    enabledInProduction: true,
    description: 'EVC batch transactions using wagmi-evc',
  },
  [FeatureFlag.EVC_SIMULATION]: {
    enabledInProduction: true,
    description: 'Transaction simulation before execution',
  },
  [FeatureFlag.EVC_DEBUG_TRACING]: {
    enabledInProduction: false,
    description: 'Debug trace calls for transaction analysis (dev only)',
  },
  [FeatureFlag.NATIVE_HYPE_DEPOSITS]: {
    enabledInProduction: true,
    description: 'Deposit native HYPE via EVC wrap',
  },
  [FeatureFlag.NATIVE_HYPE_WITHDRAWALS]: {
    enabledInProduction: true,
    description: 'Withdraw to native HYPE via EVC unwrap',
  },
  [FeatureFlag.NATIVE_HYPE_BORROWS]: {
    enabledInProduction: true,
    description: 'Borrow and receive native HYPE via EVC unwrap',
  },
  [FeatureFlag.MULTIPLY_POSITIONS]: {
    enabledInProduction: true,
    description: 'Leverage/multiply positions',
  },
  [FeatureFlag.EEARN_VAULTS]: {
    enabledInProduction: true,
    description: 'Euler Earn vault support',
  },
  [FeatureFlag.EULER_SWAP]: {
    enabledInProduction: true,
    description: 'Euler Swap integration',
  },
  [FeatureFlag.SUB_ACCOUNTS]: {
    enabledInProduction: true,
    description: 'EVC sub-account support for position isolation',
  },
  [FeatureFlag.PERMIT2]: {
    enabledInProduction: true,
    description: 'Permit2 for gasless approvals',
  },
  [FeatureFlag.SHOW_WAITLIST_REFERRALS]: {
    enabledInProduction: false,
    enabledInDevelopment: false,
    description: 'Show waitlist referral codes, position/rank, and boost score on card views',
  },
  [FeatureFlag.CARD_HOOK_MANAGEMENT]: {
    enabledInProduction: false,
    description: 'Post-settlement hook management (swap hooks) on card dashboard',
  },
}

// =============================================================================
// Feature Flag Functions
// =============================================================================

/**
 * Check if a feature flag is enabled.
 * In development mode, all features are enabled unless testing feature flags.
 */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  const config = featureFlagConfig[flag]

  if (areAllFeaturesEnabled) {
    return config?.enabledInDevelopment !== false
  }
  
  return config?.enabledInProduction ?? false
}

/**
 * Check if multiple feature flags are all enabled.
 */
export function areAllFeaturesEnabledCheck(flags: FeatureFlag[]): boolean {
  return flags.every(isFeatureEnabled)
}

/**
 * Check if any of the feature flags are enabled.
 */
export function isAnyFeatureEnabled(flags: FeatureFlag[]): boolean {
  return flags.some(isFeatureEnabled)
}

/**
 * Get all enabled feature flags.
 */
export function getEnabledFeatures(): FeatureFlag[] {
  return Object.values(FeatureFlag).filter(isFeatureEnabled)
}

/**
 * Get all disabled feature flags.
 */
export function getDisabledFeatures(): FeatureFlag[] {
  return Object.values(FeatureFlag).filter(flag => !isFeatureEnabled(flag))
}

/**
 * Get feature flag configuration for debugging.
 */
export function getFeatureFlagStatus(): Record<FeatureFlag, { enabled: boolean; description?: string }> {
  return Object.values(FeatureFlag).reduce((acc, flag) => {
    acc[flag] = {
      enabled: isFeatureEnabled(flag),
      description: featureFlagConfig[flag]?.description,
    }
    return acc
  }, {} as Record<FeatureFlag, { enabled: boolean; description?: string }>)
}

// =============================================================================
// React Hook
// =============================================================================

/**
 * Hook to check if a feature is enabled.
 * Can be used in React components.
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isNativeHypeEnabled = useFeatureFlag(FeatureFlag.NATIVE_HYPE_DEPOSITS)
 *   
 *   if (!isNativeHypeEnabled) {
 *     return null
 *   }
 *   
 *   return <NativeHypeDeposit />
 * }
 * ```
 */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  // This is a static check, no need for state/effects
  return isFeatureEnabled(flag)
}

/**
 * Hook to check multiple feature flags.
 */
export function useFeatureFlags(flags: FeatureFlag[]): boolean[] {
  return flags.map(isFeatureEnabled)
}

// =============================================================================
// Development Utilities
// =============================================================================

/**
 * Log feature flag status to console (development only).
 */
export function logFeatureFlagStatus(): void {
  if (isDevelopmentMode) {
    console.group('🚩 Feature Flags Status')
    console.log('Development Mode:', isDevelopmentMode)
    console.log('All Features Enabled:', areAllFeaturesEnabled)
    console.log('Testing Feature Flags:', shouldTestFeatureFlags)
    console.table(getFeatureFlagStatus())
    console.groupEnd()
  }
}

// =============================================================================
// Convenience Exports
// =============================================================================

export const features = {
  isDev: isDevelopmentMode,
  allEnabled: areAllFeaturesEnabled,
  
  // Quick access to common feature flags
  get evcBatch() { return isFeatureEnabled(FeatureFlag.EVC_BATCH_TRANSACTIONS) },
  get evcSimulation() { return isFeatureEnabled(FeatureFlag.EVC_SIMULATION) },
  get nativeHype() { return isFeatureEnabled(FeatureFlag.NATIVE_HYPE_DEPOSITS) },
  get multiply() { return isFeatureEnabled(FeatureFlag.MULTIPLY_POSITIONS) },
  get subAccounts() { return isFeatureEnabled(FeatureFlag.SUB_ACCOUNTS) },
  get permit2() { return isFeatureEnabled(FeatureFlag.PERMIT2) },
}


